// The only place that knows how a teacher is linked to the classes they teach.
// A class is taught by whoever has an active `classroom_teachers` row for it;
// adding a class or a teacher is a data change, never a configuration one.
//
// Verified through Directus MCP on 2026-09-25:
//   - classroom_teachers: classroom → classrooms, teacher → directus_users,
//     active (boolean)
//   - classrooms: id, name, active (boolean)
// Verified 2026-10-16: classrooms.grade is a required code (R, 1 … 12). It is
// what links a class to its curriculum (see adapter gradeCodeOf).

import { readItems } from "@directus/sdk";
import { IS_PREVIEW } from "../config";
import { DENIED, problemOf } from "./problems";
import directus from "./directusClient";
import { readJson, remove, writeJson } from "./storage";

export const CLASSROOM_TEACHERS = "classroom_teachers";

// Preview has no server; one sample class stands in for the teacher's own.
const PREVIEW_CLASSROOMS = [{ id: "preview-class", name: "Grade 1A", grade: "1" }];

// [{ id, name, grade }], once each, by name. Rows whose class the teacher may
// not see are left out rather than shown half-empty. `grade` is null when it
// is unknown, and then the class is shown every approved lesson.
export const toClassrooms = (rows) =>
  [
    ...new Map(
      (rows || [])
        .map((row) => row?.classroom)
        .filter((c) => c?.id && c?.name)
        .map((c) => [
          c.id,
          {
            id: c.id,
            name: c.name,
            grade: typeof c.grade === "string" && c.grade.trim() ? c.grade.trim() : null,
          },
        ])
    ).values(),
  ].sort((a, b) => a.name.localeCompare(b.name));

const readMyClassrooms = (fields) =>
  directus.request(
    readItems(CLASSROOM_TEACHERS, {
      fields,
      filter: {
        teacher: { _eq: "$CURRENT_USER" },
        active: { _eq: true },
        classroom: { active: { _eq: true } },
      },
      limit: -1,
    })
  );

const NAME_FIELDS = ["classroom.id", "classroom.name"];

export const listMyClassrooms = async () => {
  if (IS_PREVIEW) return PREVIEW_CLASSROOMS;
  try {
    return toClassrooms(await readMyClassrooms([...NAME_FIELDS, "classroom.grade"]));
  } catch (error) {
    // A role that may not read the grade still gets its classes (each then
    // shown every approved lesson) rather than no classes at all.
    if (problemOf(error) !== DENIED) throw error;
    return toClassrooms(await readMyClassrooms(NAME_FIELDS));
  }
};

// Only class names and ids are kept on the device, per teacher, so the app
// opens offline. Cleared on sign-out.
const listKey = (userId) => `classrooms:${userId}`;
const chosenKey = (userId) => `classroom:${userId}`;

export const getCachedClassrooms = (userId) => readJson(listKey(userId), null);
export const cacheClassrooms = (userId, classrooms) =>
  writeJson(listKey(userId), classrooms);

export const getChosenClassroom = (userId) => readJson(chosenKey(userId), null);
export const chooseClassroom = (userId, classroomId) =>
  writeJson(chosenKey(userId), classroomId);

export const clearClassrooms = (userId) => {
  remove(listKey(userId));
  remove(chosenKey(userId));
};
